import React, { useEffect, useState, Suspense, lazy } from 'react';
import { HomePage } from './pages/HomePage';
import { LanguageProvider } from './i18n/LanguageContext';
import { FloatingActions } from './components/FloatingActions';
const NON_DEFAULT_LANGUAGES = ['ua', 'ru', 'ka'];
const DEPRECATED_LANGUAGES = ['ua', 'ru'];
// Lazy load sub-pages — only HomePage loads eagerly (most common entry)
const SpecialistsPage = lazy(() => import('./pages/SpecialistsPage').then(m => ({
  default: m.SpecialistsPage
})));
const CompaniesPage = lazy(() => import('./pages/CompaniesPage').then(m => ({
  default: m.CompaniesPage
})));
const OffshoreReadyPage = lazy(() => import('./pages/OffshoreReadyPage').then(m => ({
  default: m.OffshoreReadyPage
})));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage').then(m => ({
  default: m.NotFoundPage
})));
// Minimal loading fallback
function PageLoader() {
  return <div className="min-h-screen bg-white flex items-center justify-center" data-id="element-0">
      <div className="w-8 h-8 border-2 border-[var(--color-tan)] border-t-transparent rounded-full animate-spin" data-id="element-1" />
    </div>;
}
function AppContent() {
  const [currentPath, setCurrentPath] = useState(window.location.pathname);
  useEffect(() => {
    const handleLocationChange = () => {
      setCurrentPath(window.location.pathname);
    };
    window.addEventListener('popstate', handleLocationChange);
    return () => window.removeEventListener('popstate', handleLocationChange);
  }, []);
  const pathParts = currentPath.split('/').filter(Boolean);
  const firstSegment = pathParts[0];
  // Redirect deprecated languages (ua, ru) to homepage
  if (DEPRECATED_LANGUAGES.includes(firstSegment)) {
    window.location.href = '/';
    return <PageLoader data-id="element-2" />;
  }
  // Determine the page segment based on whether there's a language prefix
  let page: string | undefined;
  if (NON_DEFAULT_LANGUAGES.includes(firstSegment)) {
    // URL like /ka/specialists → page = 'specialists'
    page = pathParts[1];
  } else if (firstSegment === 'en') {
    // Legacy /en/specialists → page = 'specialists' (will be redirected by LanguageContext)
    page = pathParts[1];
  } else {
    // English (no prefix): /specialists → page = 'specialists', / → page = undefined
    page = firstSegment;
  }
  // Block old WordPress/demo pages
  const blockedPages = ['reviews', 'about-me', 'services', 'contact', 'hello-world'];
  if (blockedPages.includes(page || '')) {
    return <Suspense fallback={<PageLoader data-id="element-4" />} data-id="element-3">
        <NotFoundPage data-id="element-5" />
      </Suspense>;
  }
  if (page === 'specialists') {
    return <Suspense fallback={<PageLoader data-id="element-7" />} data-id="element-6">
        <SpecialistsPage data-id="element-8" />
      </Suspense>;
  }
  if (page === 'companies') {
    return <Suspense fallback={<PageLoader data-id="element-10" />} data-id="element-9">
        <CompaniesPage data-id="element-11" />
      </Suspense>;
  }
  if (page === 'offshore-ready') {
    return <Suspense fallback={<PageLoader data-id="element-13" />} data-id="element-12">
        <OffshoreReadyPage data-id="element-14" />
      </Suspense>;
  }
  // Show 404 for any other unknown page
  if (page && page !== '') {
    return <Suspense fallback={<PageLoader data-id="element-16" />} data-id="element-15">
        <NotFoundPage data-id="element-17" />
      </Suspense>;
  }
  return <HomePage data-id="element-18" />;
}
export function App() {
  return <LanguageProvider data-id="element-19">
      <AppContent data-id="element-20" />
      <FloatingActions data-id="element-21" />
    </LanguageProvider>;
}